import { useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { Cancel as CancelIcon } from '@mui/icons-material';
import {
  CustomButton,
  CustomTextArea,
  DrawerForm,
  DrawerFormActionSummary,
  DrawerFormActions,
  DrawerFormSection,
  StatusChip,
} from '../../../common/components';
import { useCancelSupplierInvoice } from '../../../hooks/useSupplierLedger';
import { INVOICE_DISPLAY_STATUS_LABELS, statusColor } from '../constants/supplierLedgerUiConstants';
import {
  getGeneratedPaymentOrderNumbers,
  getGeneratedPaymentOrderTooltip,
  getInvoiceDisplayStatus,
  hasGeneratedPaymentOrder,
} from '../utils/supplierLedgerHelpers';
import { formatAmount } from '../utils/supplierLedgerFormatters';
import { AmountPanel, ClassificationChips, InvoiceSummaryItem } from './SupplierLedgerShared';

const CancelInvoiceDialog = ({ invoice, providerName, open, onClose }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const cancelInvoice = useCancelSupplierInvoice();
  const invoiceCode = invoice ? `${invoice.receipt_type || '-'} ${invoice.receipt_number || '-'}` : '-';
  const displayStatus = getInvoiceDisplayStatus(invoice);
  const hasGeneratedOrder = hasGeneratedPaymentOrder(invoice);
  const activeOrderNumbers = getGeneratedPaymentOrderNumbers(invoice);

  const handleClose = () => {
    setReason('');
    setError('');
    onClose();
  };

  const submit = async () => {
    setError('');
    if (!invoice) return;
    try {
      await cancelInvoice.mutateAsync({ id: invoice.uuid, reason });
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    }
  };

  return (
    <DrawerForm
      open={open}
      onClose={handleClose}
      title="Anular factura"
      width={{ xs: '100vw', sm: 640 }}
      footer={(
        <DrawerFormActions
          summary={(
            <DrawerFormActionSummary
              label="Pendiente a anular"
              value={formatAmount(invoice?.pending_amount_ars)}
            />
          )}
        >
          <CustomButton variant="outlined" onClick={handleClose}>Cancelar</CustomButton>
          <CustomButton
            variant="danger"
            startIcon={<CancelIcon />}
            onClick={submit}
            disabled={!reason.trim() || reason.trim().length < 3}
            isLoading={cancelInvoice.isPending}
          >
            Anular
          </CustomButton>
        </DrawerFormActions>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 1.25 }, minWidth: 0 }}>
        {error && (
          <Box sx={{ mx: { xs: 1.25, md: 2 } }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}
        <DrawerFormSection
          title="Factura seleccionada"
          action={displayStatus && (
            <StatusChip
              size="small"
              color={statusColor[displayStatus] || 'default'}
              label={INVOICE_DISPLAY_STATUS_LABELS[displayStatus] || displayStatus}
              tooltip={hasGeneratedOrder ? getGeneratedPaymentOrderTooltip(invoice) : undefined}
            />
          )}
          grid={false}
        >
          {hasGeneratedOrder && (
            <Typography
              variant="body2"
              sx={{
                mb: 1.5,
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              Al anular esta factura también se anulará la orden de pago asociada
              {activeOrderNumbers.length ? `: ${activeOrderNumbers.map((number) => `#${number}`).join(', ')}` : ''}.
            </Typography>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) 190px' },
              gap: 1.5,
              alignItems: 'stretch',
              mb: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'primary.main', fontWeight: 700, lineHeight: 1.4 }}>
                Comprobante
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: 'text.primary',
                  fontWeight: 700,
                  lineHeight: 1.25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={invoiceCode}
              >
                {invoiceCode}
              </Typography>
            </Box>
            <AmountPanel label="Pendiente ARS" value={formatAmount(invoice?.pending_amount_ars)} />
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 1.5,
            }}
          >
            <InvoiceSummaryItem label="Proveedor" value={providerName || invoice?.provider_name} highlight />
            <InvoiceSummaryItem label="Concepto" value={invoice?.concept} />
            <InvoiceSummaryItem label="Fecha" value={invoice?.invoice_date} />
            <InvoiceSummaryItem label="Vencimiento" value={invoice?.due_date} />
            <InvoiceSummaryItem label="Total ARS" value={formatAmount(invoice?.total_amount_ars)} highlight />
            <InvoiceSummaryItem label="Pendiente ARS" value={formatAmount(invoice?.pending_amount_ars)} highlight />
            <Box sx={{ gridColumn: { xs: 'span 1', sm: 'span 2' } }}>
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'primary.main', fontWeight: 700, lineHeight: 1.4 }}
              >
                Clasificación
              </Typography>
              {invoice ? <ClassificationChips row={invoice} /> : '-'}
            </Box>
          </Box>
        </DrawerFormSection>
        <DrawerFormSection title="Motivo de anulación" grid={false}>
          <CustomTextArea
            label="Motivo"
            name="reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            minRows={3}
            maxRows={5}
            sx={{ mt: 0 }}
          />
        </DrawerFormSection>
      </Box>
    </DrawerForm>
  );
};

export default CancelInvoiceDialog;
